import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import { IconFile } from '@rossum/ui/icons/tabler';
import {
  Badge,
  CircularProgress,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { downloadChatFile } from '../hooks/useChatApi';
import { triggerBlobDownload } from '../utils/download';

type OutputFile = {
  filename: string;
};

type FilesDropdownProps = {
  chatId: string | null;
  outputFiles: OutputFile[];
};

type FileMenuItemProps = {
  chatId: string;
  file: OutputFile;
  onDownloaded: () => void;
};

const FileMenuItem = ({ chatId, file, onDownloaded }: FileMenuItemProps) => {
  const intl = useIntl();
  const { mutate, isLoading } = useMutation({
    mutationFn: () => downloadChatFile(chatId, file.filename),
    onSuccess: blob => {
      triggerBlobDownload(file.filename, blob);
      onDownloaded();
    },
    onError: () => {
      toast.error(
        intl.formatMessage(
          { id: 'features.chat.files.downloadFailed' },
          { filename: file.filename }
        )
      );
    },
  });

  return (
    <MenuItem
      data-cy="chat-file-item"
      onClick={() => mutate()}
      disabled={isLoading}
    >
      <ListItemIcon>
        {isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <SvgIcon fontSize="medium">
            <IconFile />
          </SvgIcon>
        )}
      </ListItemIcon>
      <Typography variant="body2">{file.filename}</Typography>
    </MenuItem>
  );
};

export const FilesDropdown = ({ chatId, outputFiles }: FilesDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasFiles = chatId !== null && outputFiles.length > 0;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (hasFiles) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        data-cy="chat-files-button"
        onClick={handleClick}
        disabled={!hasFiles}
        sx={{
          opacity: hasFiles ? 1 : 0.4,
          cursor: hasFiles ? 'pointer' : 'default',
        }}
      >
        <Badge
          badgeContent={outputFiles.length}
          color="primary"
          invisible={!hasFiles}
          max={99}
          slotProps={{
            badge: {
              style: {
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                fontSize: 10,
              },
            },
          }}
        >
          <SvgIcon>
            <IconFile />
          </SvgIcon>
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {chatId !== null &&
          outputFiles.map(file => (
            <FileMenuItem
              key={file.filename}
              chatId={chatId}
              file={file}
              onDownloaded={handleClose}
            />
          ))}
      </Menu>
    </>
  );
};
